//@flow
import * as React from 'react';
import classnames from 'classnames';
import theme from './ActionButtons.scss';
import { themr } from 'react-css-themr';
import { translate } from 'react-i18next';
import {
    Button,
    ButtonWithPopover,
    ButtonTypes
} from 'common/components/core/button';
import { dashboardColors } from 'common/enums/ColorEnum';
import { PopoverTriggerEnum } from 'common/components/core/popover/popOverFactory';
import type { TFunction } from 'react-i18next';
import entityIds from 'common/tests/entity/entityIds.json';
import Switch from 'common/components/core/switch';
import { HelpTooltip } from 'common/components/core/help';

type Props = {
    theme: Object,
    t: TFunction,
    acceptActionColor?: string,
    acceptActionLabel?: string,
    cancelActionLabel?: string,
    resetActionLabel?: string,
    acceptButtonType?: string,
    acceptErrorMessage?: string,
    cancelButtonIcon?: string,
    cancelButtonType?: string,
    resetButtonType?: string,
    acceptActionTooltip?: string,
    invert?: boolean,
    onCancel?: Function,
    onAccept?: Function,
    onReset?: Function,
    isLoading?: Function,
    disableAccept?: Function,
    disableCancel?: Function,
    disableReset?: Function,
    disableNonImportableToggle: boolean,
    onToggleNonImportableColumns?: Function,
    showNonImportableColumns: boolean,
    nonImportableTooltip?: string,
    nonImportablePrefixLabel?: string,
    nonImportableLabel?: string,
    resetIcon?: string,
    onRefresh?: Function,
    refreshActionLabel?: string,
    refreshButtonType?: string,
    refreshButtonIcon?: string,
    disableRefonRefresh?: boolean
};
class DefaultActionButtons extends React.PureComponent<Props> {
    render() {
        const {
            onCancel,
            onAccept,
            t,
            acceptActionColor,
            acceptActionLabel = t('apply'),
            cancelActionLabel = t('cancel'),
            resetActionLabel = t('reset'),
            acceptButtonType,
            acceptErrorMessage,
            cancelButtonIcon,
            cancelButtonType = ButtonTypes.Text,
            resetButtonType = ButtonTypes.Text,
            onReset,
            isLoading,
            theme,
            disableAccept,
            disableCancel,
            disableReset,
            acceptActionTooltip,
            inverted,
            onToggleNonImportableColumns,
            showNonImportableColumns,
            disableNonImportableToggle,
            nonImportableTooltip,
            nonImportablePrefixLabel,
            nonImportableLabel,
            resetIcon,
            onRefresh,
            refreshActionLabel = t('refresh'),
            refreshButtonType = ButtonTypes.Text,
            refreshButtonIcon = 'refresh',
            disableRefonRefresh
        } = this.props;
        return onAccept ||
            onReset ||
            onCancel ||
            onToggleNonImportableColumns ? (
            <div className={theme.actions}>
                {(onCancel || onRefresh || onToggleNonImportableColumns) && (
                    <div className={theme.actionsGroupWrapper}>
                        {onCancel ? (
                            <Button
                                key="cancelAction"
                                semibold
                                uppercase
                                label={cancelActionLabel}
                                buttonType={cancelButtonType}
                                icon={cancelButtonIcon}
                                theme={theme}
                                onClick={onCancel}
                                className={classnames(
                                    theme.actionBtn,
                                    theme.cancelBtn
                                )}
                                disabled={disableCancel}
                                inverted={inverted}
                                testId={entityIds.actionButtons.cancelAction}
                            />
                        ) : null}
                        {onToggleNonImportableColumns ? (
                            <div
                                className={classnames([
                                    theme.nonImportableBtnWrapper,
                                    {
                                        [theme.disabled]: disableNonImportableToggle
                                    }
                                ])}
                            >
                                <HelpTooltip
                                    tooltipText={nonImportableTooltip}
                                />
                                <span className={theme.nonImportableLabel}>
                                    {nonImportablePrefixLabel}
                                </span>
                                <Switch
                                    disabled={disableNonImportableToggle}
                                    checked={
                                        !disableNonImportableToggle &&
                                        showNonImportableColumns
                                    }
                                    label={nonImportableLabel}
                                    onChange={onToggleNonImportableColumns}
                                    theme={theme}
                                />
                            </div>
                        ) : null}
                        {onRefresh ? (
                            <Button
                                key="refreshAction"
                                semibold
                                uppercase
                                label={refreshActionLabel}
                                buttonType={refreshButtonType}
                                icon={refreshButtonIcon}
                                theme={theme}
                                onClick={onRefresh}
                                className={classnames(
                                    theme.actionBtn,
                                    theme.refreshBtn
                                )}
                                disabled={disableRefonRefresh}
                                inverted={inverted}
                                testId={entityIds.actionButtons.refreshAction}
                            />
                        ) : null}
                    </div>
                )}
                {(onReset || onAccept) && (
                    <div
                        className={classnames(
                            theme.actionsGroupWrapper,
                            theme.actionsGroupWrapperRight
                        )}
                    >
                        {onReset ? (
                            <Button
                                key="resetAction"
                                semibold
                                uppercase
                                label={resetActionLabel}
                                buttonType={resetButtonType}
                                theme={theme}
                                onClick={onReset}
                                icon={resetIcon}
                                color={dashboardColors.darkSkyBlue.name}
                                className={classnames(
                                    theme.actionBtn,
                                    theme.resetBtn
                                )}
                                disabled={disableReset}
                                testId={entityIds.actionButtons.resetAction}
                            />
                        ) : null}
                        {onAccept ? (
                            <ButtonWithPopover
                                key="acceptAction"
                                semibold
                                uppercase
                                disabled={disableAccept}
                                color={acceptActionColor}
                                label={acceptActionLabel}
                                theme={theme}
                                buttonType={acceptButtonType}
                                isPopOverOpen={!!acceptErrorMessage}
                                triggerPopoverOn={
                                    !acceptErrorMessage &&
                                    PopoverTriggerEnum.Hover
                                }
                                popOverContent={
                                    acceptErrorMessage || acceptActionTooltip
                                }
                                isLoading={isLoading}
                                onClick={onAccept}
                                className={classnames(
                                    theme.actionBtn,
                                    theme.acceptBtn
                                )}
                                inverted={inverted}
                                testId={entityIds.actionButtons.acceptAction}
                            />
                        ) : null}
                    </div>
                )}
            </div>
        ) : null;
    }
}
const ThemedActionButtons = translate()(
    themr('ActionButtons', theme)(DefaultActionButtons)
);

export default ThemedActionButtons;
export { ThemedActionButtons as ActionButtons };
